import React, { useState } from "react";
import { X } from "lucide-react";

const BookDemoModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    organisation: "",
    role: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      setError("Please fill in all required fields.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/book-demo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Something went wrong. Please try again.");
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: "", email: "", phone: "", organisation: "", role: "" });
    setSubmitted(false);
    setError("");
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={handleClose}
      data-testid="book-demo-modal-overlay"
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[480px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: "'DM Sans', sans-serif" }}
        data-testid="book-demo-modal"
      >
        {/* Header */}
        <div className="px-8 pt-8 pb-2 flex items-start justify-between">
          <div>
            <h2 className="text-[#0F0F1E] text-[24px] font-bold leading-tight">
              Book a Demo
            </h2>
            <p className="text-[#7C7E86] text-[14px] mt-1">
              See how Elevante can transform AI learning at your organisation.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="book-demo-close"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {submitted ? (
          <div className="px-8 pt-6 pb-10 text-center" data-testid="book-demo-success">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h3 className="text-[#0F0F1E] text-[20px] font-bold mb-2">Request Submitted!</h3>
            <p className="text-[#7C7E86] text-[14px] leading-relaxed max-w-[320px] mx-auto">
              Thank you, {formData.name.split(" ")[0]}! Our team will reach out to you shortly.
            </p>
            <button
              onClick={handleClose}
              className="mt-6 px-8 py-2.5 bg-[#0F0F1E] text-white text-[14px] font-semibold rounded-xl hover:bg-black transition-colors"
              data-testid="book-demo-done"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-8 pt-4 pb-8">
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-[13px] font-semibold text-[#0F0F1E] mb-1.5">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="w-full px-4 py-2.5 text-[14px] border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all"
                  data-testid="book-demo-name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-[13px] font-semibold text-[#0F0F1E] mb-1.5">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  className="w-full px-4 py-2.5 text-[14px] border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all"
                  data-testid="book-demo-email"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-[13px] font-semibold text-[#0F0F1E] mb-1.5">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 9876543210"
                  className="w-full px-4 py-2.5 text-[14px] border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all"
                  data-testid="book-demo-phone"
                />
              </div>

              {/* Organisation (optional) */}
              <div>
                <label className="block text-[13px] font-semibold text-[#0F0F1E] mb-1.5">
                  Organisation <span className="text-[#7C7E86] font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  name="organisation"
                  value={formData.organisation}
                  onChange={handleChange}
                  placeholder="Company or institution name"
                  className="w-full px-4 py-2.5 text-[14px] border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all"
                  data-testid="book-demo-organisation"
                />
              </div>

              {/* Role (optional) */}
              <div>
                <label className="block text-[13px] font-semibold text-[#0F0F1E] mb-1.5">
                  Role in Organisation <span className="text-[#7C7E86] font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  placeholder="e.g. Dean, CTO, L&D Head"
                  className="w-full px-4 py-2.5 text-[14px] border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all"
                  data-testid="book-demo-role"
                />
              </div>
            </div>

            {error && (
              <p className="mt-3 text-[13px] text-red-500 font-medium" data-testid="book-demo-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-6 w-full py-3 bg-[#8B5CF6] text-white text-[15px] font-bold rounded-xl hover:bg-[#7C3AED] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="book-demo-submit"
            >
              {submitting ? "Submitting..." : "Request a Demo"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default BookDemoModal;
